import { BarChart3 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiURL } from '../../../../utilities/URLs'
import { account } from '../../../../classes/Account'
import { refreshAccessToken } from '../../../../utilities/extensions'
import { QuarterBreakdown } from '../classes/QuarterBreakdown'
import CountdownBadge from './CountdownBadge'

export default function QuarterGrid({ title, quarter, pendingTranscriptHold = false }) {
  const { applicationId } = useParams()
  const navigate = useNavigate()

  const [quarterBreakdown, setQuarterBreakdown] = useState(new QuarterBreakdown())
  const [isClosed, setIsClosed] = useState(null) // null = loading, true = closed, false = open
  const [countdownData, setCountdownData] = useState({ isStarted: false, daysRemaining: null })
  const [countdownLoading, setCountdownLoading] = useState(true)

  const [installmentState, setInstallmentState] = useState({ canSetup: false, planId: null, planStatus: null })

  useEffect(() => {
    const handleGetQuarterBreakdown = async () => {
      const breakdownResponse = await fetch(`${apiURL}/api/applications/${applicationId}/quarter-breakdown/${quarter}/`, {
        method: 'GET',
        headers: {
          'Authorization': account.accessToken
        }
      })
      const breakdownData = await breakdownResponse.json()
      if (breakdownData.response.error) {
        if (breakdownData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetQuarterBreakdown() : navigate('/', { state: { expired: true } })
        } else
          console.error(breakdownData.response.error)
      } else
        setQuarterBreakdown(new QuarterBreakdown(breakdownData.response.quarterBreakdown))
    }

    const handleGetClosed = async () => {
      const closedResponse = await fetch(`${apiURL}/api/applications/${applicationId}/quarter-breakdown/statuses/${quarter}/closed/`, {
        method: 'GET',
        headers: {
          'Authorization': account.accessToken
        }
      })
      const data = await closedResponse.json()
      if (!data.response.error)
        setIsClosed(data.response.isClosed)
      else
        setIsClosed(false) // Default to open if error
    }

    const handleGetCountdown = async () => {
      setCountdownLoading(true)
      const countdownResponse = await fetch(`${apiURL}/api/applications/${applicationId}/quarter-breakdown/statuses/${quarter}/countdown/`, {
        method: 'GET',
        headers: {
          'Authorization': account.accessToken
        }
      })
      const data = await countdownResponse.json()
      if (!data.response.error)
        setCountdownData(data.response)
      setCountdownLoading(false)
    }

    const handleGetInstallmentState = async () => {
      const res = await fetch(`${apiURL}/api/applications/${applicationId}/payment-installments/quarter/${quarter}`, {
        headers: { Authorization: account.accessToken }
      })
      const data = await res.json()
      if (data.response?.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshed = await refreshAccessToken()
          if (refreshed && refreshed.length > 0) return handleGetInstallmentState()
        }
        return
      }
      setInstallmentState({
        canSetup: Boolean(data.response.canSetup),
        planId: data.response.planId || null,
        planStatus: data.response.planStatus || null
      })
    }

    handleGetQuarterBreakdown()
    handleGetClosed()
    handleGetCountdown()
    handleGetInstallmentState()
  }, [])

  // Don't render if closed or still loading closed status
  if (isClosed === null || isClosed === true) return null

  return (
    <div className='col-span-1 w-full h-full flex flex-col bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 shadow-sm rounded-2xl p-6 transition-colors duration-300'>
      <div className='flex items-center justify-between mb-4 border-b border-gray-100 dark:border-slate-700 pb-3'>
        <div className='flex items-center gap-2'>
          <BarChart3 className='w-5 h-5 text-slate-600 dark:text-slate-300' />
          <h3 className='text-xl font-bold text-slate-800 dark:text-gray-100'>{title}</h3>
        </div>
        <CountdownBadge
          isStarted={countdownData.isStarted}
          isPaused={countdownData.isPaused}
          daysRemaining={countdownData.daysRemaining}
          loading={countdownLoading}
          compact={true}
        />
      </div>
      <div className='flex flex-col gap-4 flex-1'>
        <div className='flex flex-col'>
          <span className='text-sm text-slate-500 dark:text-slate-400 font-semibold mb-1'>Status</span>
          <span className='font-medium text-slate-800 dark:text-gray-100'>{quarterBreakdown.status.statusId ? quarterBreakdown.status.status : 'No status'}</span>
        </div>
        {quarterBreakdown.status.statusId && (
          <div className='flex flex-col'>
            <span className='text-sm text-slate-500 dark:text-slate-400 font-semibold mb-1'>Sub-Status</span>
            <span className='font-medium text-slate-800 dark:text-gray-100'>{quarterBreakdown.subStatus.subStatusId ? quarterBreakdown.subStatus.status : 'No action at this time'}</span>
          </div>
        )}
        {installmentState.planId && (
          <div className='flex flex-col'>
            <span className='text-sm text-slate-500 dark:text-slate-400 font-semibold mb-1'>Payment plan</span>
            <span className='font-medium text-slate-800 dark:text-gray-100 capitalize'>{installmentState.planStatus || 'Active'}</span>
          </div>
        )}
      </div>
      {pendingTranscriptHold && (
        <div className='w-full mt-auto pt-4'>
          <button
            type='button'
            onClick={() => navigate(`/applications/${applicationId}/quarter-breakdown/${quarter}/transcript-review-hold`)}
            className='w-full min-h-11 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl shadow-md font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800 active:scale-95 transition-transform mb-2'
          >
            Answer transcript review questions
          </button>
        </div>
      )}
      {installmentState.canSetup && !installmentState.planId && (
        <div className='w-full mt-auto pt-4'>
          <button
            type='button'
            onClick={() => navigate(`/applications/${applicationId}/payment-installments?quarter=${quarter}`)}
            className='w-full min-h-11 py-3 bg-sky-600 hover:bg-sky-700 text-white rounded-xl shadow-md font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800 active:scale-95 transition-transform'
          >
            Set up payment installments
          </button>
        </div>
      )}
      {installmentState.planId && (
        <div className='w-full mt-auto pt-4'>
          <button
            type='button'
            onClick={() => navigate(`/applications/${applicationId}/payment-installments/${installmentState.planId}`)}
            className='w-full min-h-11 py-3 bg-ertc-primary hover:bg-orange-600 text-white rounded-xl shadow-md font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-ertc-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800 active:scale-95 transition-transform'
          >
            View payment plan
          </button>
        </div>
      )}
      {quarterBreakdown.action.actionId && (
        <div className='w-full mt-auto pt-4'>
          <button onClick={() => navigate(quarterBreakdown.action.navigate.replace('{applicationId}', applicationId).replace('{quarter}', quarter))} className='w-full min-h-11 py-3 bg-ertc-primary text-white rounded-xl shadow-md font-bold active:scale-95 transition-transform'>
            {quarterBreakdown.action.action}
          </button>
        </div>
      )}
    </div>
  )
}
